import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword } from '../api';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  ListTodo, 
  ClipboardList, 
  BarChart3, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  User,
  Key
} from 'lucide-react';

const Layout = () => {
  const { user, logout, canViewDashboard, canViewReports, canManageUsers } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/queue', icon: ListTodo, label: 'Team Queue', show: true },
    { to: '/tasks', icon: ClipboardList, label: 'Tasks', show: true },
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', show: canViewDashboard },
    { to: '/reports', icon: BarChart3, label: 'Reports', show: canViewReports },
    { to: '/admin', icon: Settings, label: 'Admin', show: canManageUsers },
  ].filter(item => item.show);

  const getRoleBadgeColor = (role) => {
    const colors = {
      l1_tech: 'bg-blue-500/10 text-blue-400 border-blue-500/50',
      l2_tech: 'bg-purple-500/10 text-purple-400 border-purple-500/50',
      inbound_sales: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/50',
      l3_tech: 'bg-orange-500/10 text-orange-400 border-orange-500/50',
      manager: 'bg-red-500/10 text-red-400 border-red-500/50',
    };
    return colors[role] || 'bg-zinc-500/10 text-zinc-400 border-zinc-500/50';
  };

  const formatRole = (role) => {
    const labels = {
      l1_tech: 'L1 Tech',
      l2_tech: 'L2 Tech',
      inbound_sales: 'Sales',
      l3_tech: 'L3 Tech',
      manager: 'Manager',
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-surface border-r border-border transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo/Header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            {sidebarOpen && (
              <h1 className="font-heading font-bold text-lg text-text-primary truncate">
                OTMotion
              </h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-md hover:bg-surface-highlight text-text-secondary hover:text-text-primary transition-colors"
              data-testid="sidebar-toggle"
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-2">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/10 text-primary border border-primary/30'
                          : 'text-text-secondary hover:text-text-primary hover:bg-surface-highlight'
                      }`
                    }
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon size={20} />
                    {sidebarOpen && <span className="font-medium">{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Profile */}
          <div className="border-t border-border p-4">
            <div className={`flex items-center gap-3 ${!sidebarOpen && 'justify-center'}`}>
              <div className="w-10 h-10 rounded-full bg-surface-highlight flex items-center justify-center flex-shrink-0">
                <User size={20} className="text-text-secondary" />
              </div>
              {sidebarOpen && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{user?.name}</p>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user?.role)}`}>
                    {formatRole(user?.role)}
                    {user?.role_level === 'lead' && ' Lead'}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className={`mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-md text-text-secondary hover:text-primary hover:bg-primary/10 transition-colors ${
                !sidebarOpen && 'justify-center'
              }`}
              data-testid="change-password-btn"
            >
              <Key size={18} />
              {sidebarOpen && <span className="text-sm font-medium">Change Password</span>}
            </button>
            <button
              onClick={handleLogout}
              className={`mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-md text-text-secondary hover:text-destructive hover:bg-destructive/10 transition-colors ${
                !sidebarOpen && 'justify-center'
              }`}
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              {sidebarOpen && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen overflow-x-hidden">
        {/* Top bar for mobile */}
        <div className="lg:hidden h-16 bg-surface border-b border-border flex items-center px-4">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md hover:bg-surface-highlight text-text-secondary"
            data-testid="mobile-menu-toggle"
          >
            <Menu size={24} />
          </button>
          <h1 className="ml-4 font-heading font-bold text-lg text-text-primary">OTMotion</h1>
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordModal onClose={() => setShowPasswordModal(false)} />
      )}
    </div>
  );
};

const PasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast.success('Password updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface border border-border rounded-lg shadow-xl" data-testid="password-modal">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-heading font-semibold text-text-primary">Change Password</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-surface-highlight text-text-secondary">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="current-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="new-password-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="confirm-password-input"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-surface-highlight text-text-primary rounded-md font-medium hover:bg-border transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="update-password-btn"
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Layout;
